import FilePicker from '@/components/atoms/FilePicker';
import iconsPng from '@/assets/img/png/icons';
import { CONTENT_THUMBNAIL_MIN_HEIGHT, CONTENT_THUMBNAIL_MIN_WIDTH } from '@/constants/contentLimits';
import { TContentDomain, TContentType } from '@/types/Content.types';
import { Box, Flex, HStack, Image, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import ContentStatusBadge, { TContentStatusBadgeValue } from './ContentStatusBadge';
import ContentTypeTag from './ContentTypeTag';
import DomainIconTiles from './DomainIconTiles';
import { POST_IMAGE_OVERLAY } from './postImageOverlay';

type Props = {
  type: TContentType;
  title: string;
  description?: string;
  authorLabel: string;
  domains?: TContentDomain[];
  existingUrl?: string | null;
  file: File | null;
  onFileChange: (file: File | null) => void;
  onRemove?: () => void;
  canRemovePersistedThumbnail?: boolean;
  isPublic: boolean;
  // Read-only mode (e.g. the preview step): no click-to-upload, no remove, no controls.
  readOnly?: boolean;
  // When set, a status chip is overlaid at the bottom of the card (the content list).
  status?: TContentStatusBadgeValue | null;
  postComposer?: boolean;
  compactComposer?: boolean;
};

// Thumbnail card mirroring the creator-flow design: a gradient placeholder with the
// title + author overlay until an image is chosen, then the image with the same
// overlay. Clicking the card opens the file picker (unless readOnly).
const ThumbnailPreview = ({
  type,
  title,
  description,
  authorLabel,
  domains = [],
  existingUrl,
  file,
  onFileChange,
  onRemove,
  canRemovePersistedThumbnail = true,
  isPublic,
  readOnly = false,
  status,
  postComposer = false,
  compactComposer = false,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  // Derive a displayable URL straight from the picked file, so the card renders the
  // image even without the FilePicker preview (e.g. after navigating back, or on the
  // read-only preview step). Created in an effect (not useMemo) so the URL is recreated
  // on every mount — StrictMode's mount→unmount→remount would otherwise revoke it.
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setFileUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // The picker may preview the original HEIC, while `file` is the displayable JPEG
  // produced by compression. Prefer the latter once it is available.
  const image = fileUrl || preview || existingUrl || undefined;
  const hasImage = Boolean(image);
  const isPostImage = type === 'post' && hasImage;
  const typeIcon = {
    article: iconsPng.creatorArticleWhite,
    post: iconsPng.creatorPostWhite,
    video: iconsPng.creatorReelWhite,
  }[type];
  const emptyBackground = postComposer
    ? 'linear-gradient(175deg, #4E1AEF 0%, #6718E2 35%, #AF578B 70%, #E67436 100%)'
    : 'linear-gradient(180deg, #4E1AEF 0%, #835FF4 20%, #CABAFA 40%, #FFFFFF 60%)';

  const openPicker = () => inputRef.current?.click();

  const removeImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) onRemove();
    else onFileChange(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <VStack align="stretch" gap="8px" width="400px" maxWidth="100%" cursor={'pointer'}>
      <Box
        position="relative"
        width="400px"
        maxWidth="100%"
        aspectRatio={1}
        borderRadius="14px"
        overflow="hidden"
        cursor={readOnly ? 'default' : 'pointer'}
        onClick={readOnly ? undefined : openPicker}
        backgroundImage={isPostImage || !hasImage ? emptyBackground : undefined}
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
      >
        {/* Gradient cards: on hover (dashboard group) the purple gradient floods the
            whole card. A separate full-card layer fades in since CSS can't transition
            background-image. No-op when there's an image or outside a [role=group]. */}
        {!hasImage && !postComposer && (
          <Box
            position="absolute"
            inset={0}
            backgroundImage="linear-gradient(180deg, #4E1AEF 0%, #835FF4 40%, #A68CF7 70%, #CABAFA 100%)"
            opacity={0}
            transition="opacity 0.45s ease"
            _groupHover={{ opacity: 1 }}
          />
        )}

        {/* Real <img> layer — renders both data URLs (create preview) and remote URLs.
            Gentle zoom on hover, but only when rendered inside a [role=group] ancestor
            (the dashboard cards) — no-op in the setup/preview wizard. */}
        {hasImage && (
          <Image
            src={image}
            alt={title}
            loading="lazy"
            decoding="async"
            position="absolute"
            inset={0}
            width="100%"
            height="100%"
            objectFit="cover"
            transition="transform 0.45s ease"
            _groupHover={{ transform: 'scale(1.06)' }}
          />
        )}

        {/* Readability scrim over images — semi-transparent so the image stays visible. */}
        {hasImage && (
          <Box
            position="absolute"
            inset={0}
            backgroundImage={
              isPostImage ? POST_IMAGE_OVERLAY : 'linear-gradient(180deg, #4E1AEF 0%, rgba(78, 26, 239, 0) 100%)'
            }
          />
        )}

        {/* All overlay content lives in one padded container — adjust spacing here. */}
        <Flex position="absolute" inset={0} direction="column" padding="24px" paddingBottom={0} gap="0px">
          {/* Top: type tag + domain badges, then title + author */}
          <VStack align="flex-start" gap={'8px'} flex={1} minWidth={0}>
            <HStack justifyContent={'space-between'} width={'full'}>
              <HStack gap="8px">
                <Image src={typeIcon} alt="" width="16px" height="16px" objectFit="contain" />
                <ContentTypeTag type={type} />
              </HStack>
              {domains.length > 0 && (
                <Box flexShrink={0}>
                  <DomainIconTiles domains={domains} />
                </Box>
              )}
            </HStack>

            <Text
              fontSize={postComposer ? '24px' : '22px'}
              fontWeight={600}
              color="white"
              width="100%"
              opacity={0.8}
              fontFamily={'Lora'}
              fontStyle={'italic'}
            >
              {title || 'Title will go here'}
            </Text>
            {!postComposer && (
              <Text fontSize="14px" fontWeight={700} color="whiteAlpha.800">
                {authorLabel}
              </Text>
            )}

            {/* Description — full width, below the title/author */}
            {!compactComposer && (description || postComposer) && (
              <Text fontSize={postComposer ? '19px' : '14px'} color="white" fontWeight={600} noOfLines={5}>
                {description || 'Content text will go here'}
              </Text>
            )}
          </VStack>

          {/* Upload affordance (editable + no image) fills the remaining space below */}
          {!hasImage &&
            !readOnly &&
            (postComposer ? (
              <HStack justify="center" gap="0px" marginBottom="4px">
                <Image src={iconsPng.uploadThumbnail} alt="" width="100px" height="100px" objectFit="contain" />
                <VStack align="flex-start" gap="2px" marginLeft={'-10px'}>
                  <Text fontSize="13px" fontWeight={800} color="white">
                    UPLOAD BACKGROUND
                  </Text>
                  <Text fontSize="12px" color="whiteAlpha.900">
                    PNG, JPG, HEIC - Max 5MB
                  </Text>
                </VStack>
              </HStack>
            ) : (
              <VStack flex={1} justify="end" gap="0px" marginBottom={compactComposer ? '68px' : '40px'}>
                <Image
                  src={iconsPng.uploadThumbnail}
                  alt="Upload thumbnail"
                  width="80px"
                  height="80px"
                  objectFit="contain"
                />
                <Text fontSize="12px" fontWeight={900} color="text.mediumGray" textTransform="uppercase">
                  Upload thumbnail
                </Text>
                <Text fontSize="11px" color="text.mediumGray">
                  PNG, JPG, HEIC · Max 5MB
                </Text>
              </VStack>
            ))}
        </Flex>

        {/* Status chip overlaid at the bottom-left of the card (content list). */}
        {status && (
          <Box position="absolute" bottom="24px" right="24px">
            <ContentStatusBadge status={status} />
          </Box>
        )}
      </Box>

      {/* Controls below the card — only in editable mode */}
      {!readOnly && !postComposer && (
        <VStack justify="space-between" align="center" gap="8px">
          <Text fontSize="12px" color={compactComposer ? 'text.mediumBlue' : 'text.mediumGray'}>
            {isPublic
              ? 'Note: This content will be visible to all organizations.'
              : 'Note: This content will be visible to your organization only.'}
          </Text>
          {hasImage && (file || canRemovePersistedThumbnail) && (
            <Text
              fontSize="12px"
              fontWeight={700}
              color="extra.red"
              cursor="pointer"
              flexShrink={0}
              whiteSpace="nowrap"
              _hover={{ textDecoration: 'underline' }}
              onClick={removeImage}
            >
              Remove thumbnail
            </Text>
          )}
        </VStack>
      )}

      {!readOnly && postComposer && hasImage && (file || canRemovePersistedThumbnail) && (
        <Text
          as="button"
          type="button"
          alignSelf="center"
          color="extra.red"
          fontSize="14px"
          fontWeight={700}
          onClick={removeImage}
          marginTop="20px"
        >
          Remove background
        </Text>
      )}

      {!readOnly && (
        <FilePicker
          inputRef={inputRef}
          selectedFile={file}
          setSelectedFile={onFileChange}
          setPreview={setPreview}
          accept="image/*"
          fileMinWidthPx={CONTENT_THUMBNAIL_MIN_WIDTH}
          fileMinHeightPx={CONTENT_THUMBNAIL_MIN_HEIGHT}
          fileSizeLimitInMBs={5}
        />
      )}
    </VStack>
  );
};

export default ThumbnailPreview;
